import React, { useEffect, useState } from 'react';
import { FaArrowLeft } from "react-icons/fa"; 
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components';
import axios from '../../../api/axios';
import { sendMessageToReactNativeApp } from "../../../components/utils/sendMessageToReactNativeApp";
import { X } from "lucide-react";
import videoIcon from '../../../assets/icons/chat-video-icon.svg';
import endChatIcon from '../../../assets/icons/chat-end-icon.svg';
import endSessionIllustration from '../../../assets/icons/chat-end-session-illustration.svg';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  color: black;
  padding: 10px 10px 2px 10px;
`;

const DoctorInfo = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
`;

const DoctorName = styled.span`
  font-weight: bold;
`;

const TimeBadge = styled.span`
  font-size: 12px;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 4px 4px;
  margin-left: 0px;
  border-radius: 9999px;
`;

const Status = styled.span`
  width: 8px;
  height: 8px;
  background: #D42E1C;
  border: 1.33px solid #FFE1E1;
  border-radius: 50%;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LeftContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.img`
  width: 28px;
  height: 28px;
  cursor: pointer;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  object-fit: cover;
`;

interface ChatHeaderProps {
  doctorAvatar: string;
  doctorName: string;
  doctorSpecialty: string;
  consultationMedia: string
  startTime: string | Date;
  duration: number;
  isExpired?: boolean;
  consultationId: string;
  onEnded?: () => void;
  token?: string;
  serverTime: string | Date;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  doctorAvatar,
  doctorName,
  doctorSpecialty,
  consultationMedia,
  startTime,
  duration,
  isExpired = false,
  consultationId,
  onEnded,
  token = '',
  serverTime,
}) => {
  const navigate = useNavigate();
  const calculateRemainingTime = () => {
    const startMs = Date.parse(
      startTime instanceof Date ? startTime.toISOString() : startTime
    );
    const endMs = startMs + duration * 1000;
    const timeOffset = new Date(serverTime).getTime() - Date.now();
    const nowMs = Date.now()  + timeOffset;
    
    return Math.max(Math.floor((endMs - nowMs) / 1000), 0);
  };

  const [remainingTime, setRemainingTime] = useState<number>(calculateRemainingTime());
  const [expired, setExpired] = useState<boolean>(isExpired);

  // Timer countdown
  useEffect(() => {
    console.log('timerCountdown useEffect', expired)
    if (expired) return;

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setExpired(true);
          return 0;
        }
        console.log('setRemainingTrime', prev)
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [expired]);

  // Jika isExpired dari props berubah ke true
  useEffect(() => {
    if (isExpired) {
      setExpired(true);
      setRemainingTime(0);
    }
  }, [isExpired]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const [open, setOpen] = useState(false);

  const openDialogEndChat = () => {
    setOpen(true);
  }

  const handleEndChat = async () => {
    setOpen(false);
    try {
      await axios.post(
        '/teleconsult/end',
        { teleconsult_id: consultationId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setExpired(true);
      setRemainingTime(0);
      if (onEnded) onEnded();
      
       // 🔄 kasih delay 1 detik sebelum reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Gagal mengakhiri konsultasi:', error);
      alert('Terjadi kesalahan saat mengakhiri konsultasi.');
    }
  };

  const handleVideoCall = () => {
    navigate(`/video-call-ui/${consultationId}/${token}`);
  }

  const handleBack = () => {
    sendMessageToReactNativeApp({ action: "goBack" });
  };

  return (
    <HeaderContainer>
      <LeftContent>
        {/* <Avatar src={doctorAvatar} alt="Dokter" /> */}
        <button
          onClick={handleBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            marginRight: "8px"
          }}
        >
          <FaArrowLeft size={20} color="#000" />
        </button> 
        <DoctorInfo>
          <DoctorName>{doctorName}</DoctorName>
          <StatusWrapper>
            {!expired && <Status />}
            <TimeBadge>{formatTime(remainingTime)}</TimeBadge>
          </StatusWrapper>
        </DoctorInfo>
      </LeftContent>

      {!expired && (
        <IconWrapper>
          {consultationMedia !== 'Chat' && 
            (
              <IconButton
                src={videoIcon}
                alt="Video Call"
                onClick={handleVideoCall}
              />
            ) 
            }
            <IconButton
              src={endChatIcon}
              alt="End Chat"
              onClick={openDialogEndChat}
            />
          </IconWrapper>
      )}

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-end">
          <div className="bg-white w-full rounded-t-2xl p-6 relative">
            {/* Tombol close (X) */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <img
                src={endSessionIllustration}
                alt="End Chat"
                className="w-24 h-24"
              />
            </div>

            {/* Judul */}
            <p className="font-semibold text-lg text-center mb-2">
              Akhiri Sesi Konsultasi?
            </p>

            {/* Deskripsi */}
            <p className="text-gray-600 text-sm text-center mb-6">
              Apakah Anda yakin ingin menutup sesi konsultasi? Semua percakapan akan
              berakhir dan pastikan semua kebutuhan, termasuk resep dan rujukan,
              sudah terpenuhi.
            </p>

            {/* Tombol */}
            <div className="flex gap-3">
              <button
                onClick={handleEndChat}
                className="flex-1 border border-red-500 text-red-500 font-medium py-2 rounded-full"
              >
                Akhiri
              </button>
              <button 
                onClick={() => setOpen(false)}
                className="flex-1 bg-red-500 text-white font-medium py-2 rounded-full">
                Lanjutkan
              </button>
            </div>
          </div>
        </div>
      )}


      
    </HeaderContainer>
  );
};

export default ChatHeader;
